// Form-definition types for the admin Apps tab. Per-app form files at
// admin/ui/src/app_configs/{app_code}.tsx export a FormDefinition matching this
// shape; the Apps tab renders the form from it.
//
// Schema lives with the form (label, description, destructive flags) NOT in the
// platform package — those are admin-UI metadata, not runtime concerns. See
// SPEC §7.1 for the duplication pattern between this file and the consumer's
// canonical config schema (e.g. captomi-office/src/config/app-config.ts).

export type ConfigValue = boolean | string | number;
export type ConfigPayload = Record<string, ConfigValue>;

export interface ToggleDefinition {
  /** Field key in the config payload — must match the consumer's Zod schema. */
  key: string;
  /** Primitive type of the field. v1 supports boolean / string / number. */
  type: 'boolean' | 'string' | 'number';
  /** Human-readable label shown in the form. */
  label: string;
  /** Optional helper text under the field. */
  description?: string;
  /** Default value shown when creating a new config doc. */
  default: boolean | string | number;
  /**
   * If present, the Save action shows a confirmation modal requiring typing
   * CONFIRM when this toggle changes in the matched direction.
   *   - 'enabled' = only when boolean flips false → true
   *   - 'changed' = on any change
   */
  destructive?: {
    when: 'enabled' | 'changed';
    confirm_message: string;
  };
}

/** One choice in a SelectField. Picking it writes `set` — one OR MORE config keys at once,
 *  so a single operator choice can drive several underlying keys (e.g. a "Model" pick that
 *  sets both the LLM vendor and the model id). */
export interface SelectOption {
  /** Stable id, used as the <option> value and to identify the current selection. */
  id: string;
  /** Human-readable label shown in the dropdown. */
  label: string;
  /** Optional helper text shown when this option is the current selection. */
  description?: string;
  /** Config key→value writes this option applies on save. */
  set: Record<string, ConfigValue>;
}

/** A single dropdown that maps one operator choice onto one-or-more config keys. Unlike a
 *  ToggleDefinition it owns no single `key` — its options' `set` maps own the keys. */
export interface SelectField {
  type: 'select';
  /** Field id — NOT a config key (a select owns its keys via the options' `set`). */
  id: string;
  label: string;
  description?: string;
  /** The config keys this field controls. Used to derive the current option + nothing else. */
  keys: string[];
  options: SelectOption[];
  /** Option id selected when the config keys match no option (fresh doc / off-menu value). */
  default_id: string;
}

export type Field = ToggleDefinition | SelectField;

export interface FormDefinition {
  app_code: string;
  toggles: Field[];
}

/** The option id whose `set` fully matches the current config, or `default_id` when none
 *  match (e.g. a fresh doc, or an off-menu value set out-of-band). */
export function selected_option_id(field: SelectField, values: ConfigPayload): string {
  const match = field.options.find((o) =>
    Object.entries(o.set).every(([k, v]) => values[k] === v),
  );
  return match?.id ?? field.default_id;
}
